import { FC } from 'react';
import { Button, Divider, Stack, Typography, Box } from '@mui/material';
import { ForgotPasswordLink, FormBox, FormContent, GoogleButton, Title, TabContainer, TabButton } from './styles';
import theme from '@/theme';
import { LargeTextfield } from '@/components/CustomTextfield/styles';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { doc, getDoc } from 'firebase/firestore';
import { doSignInWithEmailAndPassword, doSignInWithGoogle } from '@/firebase/auth';
import { db } from '@/firebase/firebase';
import { GoogleIcon } from '@/components/Icons/Icons';
import PasswordField from './PasswordField';
import { LoginSchema, getLoginSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import useAlertStore from '@/stores/alertStore';
import { errorReporter } from '@/services/errorReportingService';
import { throwLoginErrors } from '@/helpers/loginErrors';
import { FirebaseError } from 'firebase/app';
import { InfoOutlined } from '@mui/icons-material';
import { clearLogoutFlag } from '@/utils/crossTabAuthSync';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RouteMap } from '@/routes/Routes';

interface LoginFormProps {
  setShowLogin: (value: boolean) => void;
  setShowForgotPassword: (value: boolean) => void;
  redirectedFromSharedNote?: boolean;
  showLogin: boolean;
}

const LoginForm: FC<LoginFormProps> = ({
  setShowLogin,
  setShowForgotPassword,
  redirectedFromSharedNote,
  showLogin,
}) => {
  const { showAlert } = useAlertStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const noteId = searchParams.get('noteId');
  const redirectUrl = noteId ? `${RouteMap.NOTE}/${noteId}` : RouteMap.HOME;

  const messages = {
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email address',
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginSchema>({
    resolver: zodResolver(getLoginSchema(messages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<LoginSchema> = async (data) => {
    // Clear logout flags BEFORE initiating login to prevent onAuthStateChanged
    // from immediately signing out the user (fixes extension logout → login loop)
    sessionStorage.removeItem('userRequestedLogout');
    clearLogoutFlag();

    await doSignInWithEmailAndPassword(data.email, data.password)
      .then((res) => {
        if (res?.user) {
          // The redirection is handled by the Login component's useEffect
        }
      })
      .catch((error) => {
        const errorMessage = throwLoginErrors(error as FirebaseError);
        showAlert(errorMessage.message, 'error');
        // Login error handled by alert
      });
  };

  const handleGoogleSignIn = async () => {
    // Clear logout flags BEFORE initiating login to prevent onAuthStateChanged
    // from immediately signing out the user (fixes extension logout → login loop)
    sessionStorage.removeItem('userRequestedLogout');
    clearLogoutFlag();

    try {
      const { result: res, isNewUser, backendUserId } = await doSignInWithGoogle();
      if (res?.user) {
        // Read before the onboarding lookup below: Login's redirect effect consumes
        // redirectAfterLogin as soon as the session resolves, which can land during
        // that await — reading it afterwards sent onboarding users to Home.
        const storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');

        // Check if user needs onboarding (same logic as SignUpForm)
        let needsOnboarding = false;

        if (isNewUser) {
          needsOnboarding = true;
        } else if (backendUserId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', backendUserId));
            const status = userDoc.exists() ? userDoc.data()?.onboardingStatus || null : null;
            needsOnboarding = !status;
          } catch (error: unknown) {
            // Firestore read failed — assume onboarding needed to avoid skipping it.
            // Report it: this silently re-onboards an established user, and
            // without telemetry the cause is invisible.
            errorReporter.expected('Failed to read onboarding status during login', error, {
              feature: 'onboarding',
              operation: 'readOnboardingStatus',
            });
            needsOnboarding = true;
          }
        }

        if (needsOnboarding) {
          if (storedRedirectUrl) {
            sessionStorage.setItem('postOnboardingRedirect', storedRedirectUrl);
            sessionStorage.removeItem('redirectAfterLogin');
          } else if (noteId) {
            sessionStorage.setItem('postOnboardingRedirect', redirectUrl);
          }
          navigate(RouteMap.ONBOARDING);
          return;
        }
        // Existing user with completed onboarding → Login.tsx useEffect handles redirect to HOME
      }
    } catch (error) {
      const errorMessage = throwLoginErrors(error as FirebaseError);
      showAlert(errorMessage.message, 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormBox>
        <TabContainer>
          <TabButton active={!showLogin} onClick={() => setShowLogin(false)}>
            Sign Up
          </TabButton>
          <TabButton active={showLogin} onClick={() => setShowLogin(true)}>
            Log In
          </TabButton>
        </TabContainer>

        <FormContent>
          <Stack>
            <Title>Log In</Title>
            {redirectedFromSharedNote && (
              <Box
                sx={{
                  marginTop: 2,
                  padding: '12px 20px',
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <InfoOutlined
                  sx={{
                    color: theme.palette.info.main,
                    fontSize: '20px',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.info.main,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }}
                >
                  Please log in to access this shared note.
                </Typography>
              </Box>
            )}
            <GoogleButton
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              sx={{ mt: 3 }}
            >
              Google
            </GoogleButton>

            <Divider sx={{ mt: 2 }}>or enter your details</Divider>
          </Stack>

          <Stack>
            <Controller
              name={'email'}
              control={control}
              render={({ field }) => (
                <LargeTextfield
                  {...field}
                  placeholder="Email Address"
                  variant="outlined"
                  fullWidth
                  sx={{ marginBottom: '34px' }}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />

            <Controller
              name={'password'}
              control={control}
              render={({ field }) => (
                <PasswordField
                  {...field}
                  variant="outlined"
                  fullWidth
                  placeholder="Password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />
              )}
            />

            <ForgotPasswordLink variant="body1" underline="hover" onClick={() => setShowForgotPassword(true)}>
              Forgot password?
            </ForgotPasswordLink>
          </Stack>

          <Button variant="contained" color="primary" fullWidth size="large" type="submit">
            Continue
          </Button>
        </FormContent>
      </FormBox>
    </form>
  );
};

export default LoginForm;
